<template>
  <div class="min-h-screen bg-white">
    <!-- Category Hero Header -->
    <section class="bg-slate-900 py-10 md:py-16 relative overflow-hidden text-white">
      <div class="absolute inset-0 bg-red-600/10 -skew-y-3 origin-top-left"></div>
      <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <nav class="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-6">
          <NuxtLink to="/" class="hover:text-red-500 transition-colors">Home</NuxtLink>
          <span class="w-1 h-1 rounded-full bg-slate-700"></span>
          <NuxtLink to="/shop" class="hover:text-red-500 transition-colors">Shop</NuxtLink>
          <span class="w-1 h-1 rounded-full bg-slate-700"></span>
          <span class="text-white">{{ categoryName }}</span>
        </nav>
        <h1 class="text-3xl md:text-5xl font-bold tracking-tight mb-4">{{ categoryName }}</h1>
        <p class="text-slate-300 max-w-2xl text-sm leading-relaxed">
          Certified {{ categoryName.toLowerCase() }} designed for ultimate safety and compliance in Qatar. 
          Browse our selection of top-tier industrial equipment approved by the Civil Defense.
        </p>
      </div>
    </section>

    <!-- Product Grid -->
    <main class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <div v-if="products && products.length > 0" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <div 
          v-for="product in products" 
          :key="product.id" 
          class="group flex flex-col h-full bg-white rounded-lg p-2 transition-all duration-300 hover:shadow-xl hover:shadow-slate-200 border border-slate-100 hover:border-slate-300"
        >
          <div class="relative aspect-square rounded bg-slate-50 overflow-hidden p-3 flex items-center justify-center cursor-pointer" @click="navigateTo(`/product/${product.slug || product.id}`)">
            <NuxtImg :src="product.image" :alt="product.name" format="webp" loading="lazy" class="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500" />
          </div>
          <div class="pt-3 pb-1 space-y-1 flex-1 flex flex-col">
            <div class="text-red-600 text-[8px] font-bold uppercase tracking-wider">{{ product.brand || 'Security Solutions' }}</div>
            <h3 class="text-xs font-bold text-slate-900 leading-tight hover:text-red-600 cursor-pointer" @click="navigateTo(`/product/${product.slug}`)">{{ product.name }}</h3>
            <div class="mt-auto pt-2 flex items-center justify-between">
              <span class="text-xs font-bold text-slate-900">QAR {{ (product.offerPrice || product.price || 0).toFixed(2) }}</span>
              <button class="text-slate-400 hover:text-red-600 transition-colors">
                <component :is="ShoppingCartIcon" class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-20">
        <h2 class="text-xl font-bold text-slate-900">No {{ categoryName }} found.</h2>
        <p class="text-slate-500 mt-2">Try browsing other categories in our catalog.</p>
        <NuxtLink to="/shop" class="inline-flex mt-6 px-6 py-2 bg-slate-900 text-white text-xs font-bold uppercase tracking-widest rounded-lg">View Full Catalog</NuxtLink>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useAsyncData, useSeoMeta } from '#app'
import { ShoppingCart as ShoppingCartIcon } from 'lucide-vue-next'
import { useSeoStore } from '~/stores/seo'

const route = useRoute()
const slug = computed(() => route.params.slug as string)
const seoStore = useSeoStore()

// Load global SEO settings for category templates
if (process.client && !seoStore.settings?.companyName) {
  seoStore.fetchSettings()
}

// Capitalize slug for display (e.g. fire-alarms -> Fire Alarms)
const categoryName = computed(() => {
  return slug.value.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
})

// SSR Fetch Products for this Category
const { data: products } = await useAsyncData(`category-${slug.value}`, () => {
  return $fetch(`/api/categories/${slug.value}`)
})

// Use global SEO templates for category pages
const replaceTokens = (template: string) => {
  if (!template) return ''
  return template
    .replace(/\{categoryName\}/gi, categoryName.value)
    .replace(/\{companyName\}/gi, seoStore.settings?.companyName || 'Best Alarm Supplies')
}

// Inject Schema.org and Meta SEO
const canonicalUrl = `https://alarmsupplies.qa/category/${slug.value}`
const categoryTitle = seoStore.settings?.categoryTitleTemplate 
  ? replaceTokens(seoStore.settings.categoryTitleTemplate) 
  : `Buy ${categoryName.value} in Qatar | Certified Fire Safety Equipment`
const categoryDescription = seoStore.settings?.categoryDescriptionTemplate 
  ? replaceTokens(seoStore.settings.categoryDescriptionTemplate) 
  : `Shop the best ${categoryName.value.toLowerCase()} in Doha, Qatar. Civil Defense approved safety equipment with competitive pricing and rapid delivery.`

useSeoMeta({
  title: categoryTitle,
  description: categoryDescription
})

useHead({
  link: [{ rel: 'canonical', href: canonicalUrl }]
})

if (products.value && products.value.length > 0) {
  useSchemaOrg([
    defineWebPage({
      url: canonicalUrl
    }),
    defineBreadcrumb({
      itemListElement: [
        { name: 'Home', item: 'https://alarmsupplies.qa/' },
        { name: 'Shop', item: 'https://alarmsupplies.qa/shop' },
        { name: categoryName.value, item: canonicalUrl }
      ]
    }),
    defineCollectionPage({
      name: `Buy ${categoryName.value} in Qatar`,
      description: `Shop the best ${categoryName.value.toLowerCase()} in Doha, Qatar.`,
    }),
    defineItemList({
      itemListElement: products.value.map((p: any, index: number) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://alarmsupplies.qa/product/${p.slug || p.id}`,
        name: p.name,
        image: p.image
      }))
    }),
    defineWebPage({
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `Are the ${categoryName.value.toLowerCase()} approved by Qatar Civil Defense?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Yes, all our ${categoryName.value.toLowerCase()} comply with Qatar Civil Defense regulations and international safety standards.`
          }
        },
        {
          '@type': 'Question',
          name: `Do you provide installation and delivery for ${categoryName.value.toLowerCase()}?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `We offer fast delivery across Doha and Qatar. Professional installation services for ${categoryName.value.toLowerCase()} can also be arranged.`
          }
        }
      ]
    })
  ])
}
</script>
